import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Appointment, AppointmentFormData } from '../../models/Appointment';
import { Client } from '../../models/Client';
import { format } from 'date-fns';

// Minimal shape needed to populate the RBT picker.
interface RbtOption {
  id: string;
  name: string;
}

interface AddAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (appointmentData: AppointmentFormData) => Promise<void>;
  clients: Client[];
  rbts?: RbtOption[];
  // Pre-select a client when opening (e.g. booking from a specific client's page).
  preselectedClientId?: string;
  // When provided, the modal edits/reschedules this appointment instead of creating.
  initialAppointment?: Appointment;
}

const blankForm = (): AppointmentFormData => ({
  clientId: '',
  rbtId: '',
  startDate: format(new Date(), 'yyyy-MM-dd'),
  startTime: '09:00',
  endTime: '12:00',
  notes: '',
  supervisionRequired: true,
  cameraRequired: true,
  isRecurring: false,
  recurrenceRule: '',
});

export const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  clients,
  rbts = [],
  preselectedClientId,
  initialAppointment,
}) => {
  const isEdit = !!initialAppointment;
  const [formData, setFormData] = useState<AppointmentFormData>(blankForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      return;
    }
    // Opening: prefill from the appointment being edited, else a pre-selected
    // client (with its default RBT), else a blank form.
    if (initialAppointment) {
      setFormData({
        clientId: initialAppointment.clientId,
        rbtId: initialAppointment.rbtId,
        startDate: format(initialAppointment.startTime, 'yyyy-MM-dd'),
        startTime: format(initialAppointment.startTime, 'HH:mm'),
        endTime: format(initialAppointment.endTime, 'HH:mm'),
        notes: initialAppointment.notes || '',
        supervisionRequired: initialAppointment.supervisionRequired,
        cameraRequired: initialAppointment.cameraRequired,
        isRecurring: initialAppointment.isRecurring,
        recurrenceRule: initialAppointment.recurrenceRule || '',
      });
    } else if (preselectedClientId) {
      const c = clients.find((cl) => cl.id === preselectedClientId);
      setFormData({
        ...blankForm(),
        clientId: preselectedClientId,
        rbtId: c?.assignedRbtIds?.[0] || '',
      });
    } else {
      setFormData(blankForm());
    }
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialAppointment, preselectedClientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate time
    const [startHour, startMin] = formData.startTime.split(':').map(Number);
    const [endHour, endMin] = formData.endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes <= startMinutes) {
      setError('End time must be after start time');
      return;
    }

    if (!formData.clientId) {
      setError('Please select a client');
      return;
    }

    // If the client has no assigned RBT, one must be chosen here.
    const client = clients.find((c) => c.id === formData.clientId);
    const clientHasRbt = !!client?.assignedRbtIds?.length;
    if (!clientHasRbt && !formData.rbtId) {
      setError('This client has no assigned RBT. Please select an RBT.');
      return;
    }

    setLoading(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = () => {
    const [startHour, startMin] = formData.startTime.split(':').map(Number);
    const [endHour, endMin] = formData.endTime.split(':').map(Number);
    const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  const selectedClient = clients.find((c) => c.id === formData.clientId);
  const clientHasAssignedRbt = !!selectedClient?.assignedRbtIds?.length;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: 'var(--spacing-4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-6)',
          }}
        >
          <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>
            {isEdit ? 'Reschedule Appointment' : 'Book Appointment'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-400)',
              padding: 'var(--spacing-2)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="clientId">
              Client *
            </label>
            <select
              id="clientId"
              className="form-input"
              value={formData.clientId}
              onChange={(e) => {
                const clientId = e.target.value;
                // Default the RBT to the client's assigned RBT (if any); the user
                // can override it below.
                const c = clients.find((cl) => cl.id === clientId);
                const defaultRbt = c?.assignedRbtIds?.[0] || '';
                setFormData({ ...formData, clientId, rbtId: defaultRbt });
              }}
              required
              disabled={isEdit}
            >
              <option value="">Select a client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.firstName} {client.lastName} - {client.address}, {client.city}
                </option>
              ))}
            </select>
            {clients.length === 0 && (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-warning)', marginTop: 'var(--spacing-2)' }}>
                No clients available. Please add clients first.
              </p>
            )}
          </div>

          {/* RBT — defaults to the client's assigned RBT, but can be set here in
              case the client has none assigned yet (or to book a different one). */}
          <div className="form-group">
            <label className="form-label" htmlFor="rbtId">
              RBT{clientHasAssignedRbt ? '' : ' *'}
            </label>
            <select
              id="rbtId"
              className="form-input"
              value={formData.rbtId || ''}
              onChange={(e) => setFormData({ ...formData, rbtId: e.target.value })}
            >
              <option value="">
                {clientHasAssignedRbt ? "Use client's assigned RBT" : 'Select an RBT'}
              </option>
              {rbts.map((r) => {
                const assigned = selectedClient?.assignedRbtIds?.includes(r.id);
                return (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {assigned ? ' (assigned)' : ''}
                  </option>
                );
              })}
            </select>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', marginTop: 'var(--spacing-2)' }}>
              {!formData.clientId
                ? 'Select a client first.'
                : clientHasAssignedRbt
                ? "Defaults to the client's assigned RBT. Change it to book with a different RBT."
                : 'This client has no assigned RBT — choose one to book this appointment.'}
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="startDate">
              Date *
            </label>
            <input
              id="startDate"
              type="date"
              className="form-input"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              min={format(new Date(), 'yyyy-MM-dd')}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="startTime">
                Start Time *
              </label>
              <input
                id="startTime"
                type="time"
                className="form-input"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="endTime">
                End Time *
              </label>
              <input
                id="endTime"
                type="time"
                className="form-input"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>
          </div>

          <div
            style={{
              padding: 'var(--spacing-3)',
              backgroundColor: 'var(--color-light-accent)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)', fontWeight: 'var(--font-medium)' }}>
              Duration: {calculateDuration()}
            </p>
          </div>

          <div
            style={{
              marginTop: 'var(--spacing-4)',
              marginBottom: 'var(--spacing-4)',
              paddingTop: 'var(--spacing-4)',
              borderTop: '1px solid var(--color-gray-200)',
            }}
          >
            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)' }}>
              Session Requirements
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.supervisionRequired}
                onChange={(e) => setFormData({ ...formData, supervisionRequired: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontSize: 'var(--text-base)' }}>Supervision Required</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.cameraRequired}
                onChange={(e) => setFormData({ ...formData, cameraRequired: e.target.checked })}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontSize: 'var(--text-base)' }}>Camera Streaming Required</span>
            </label>
          </div>

          <div className="form-group" style={{ marginTop: 'var(--spacing-4)' }}>
            <label className="form-label" htmlFor="notes">
              Notes (Optional)
            </label>
            <textarea
              id="notes"
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any notes about this appointment..."
              rows={3}
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              marginTop: 'var(--spacing-6)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ flex: 1 }}
              disabled={loading || clients.length === 0}
            >
              {loading
                ? isEdit
                  ? 'Saving...'
                  : 'Booking...'
                : isEdit
                ? 'Save Changes'
                : 'Book Appointment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
